import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Clock, AlertTriangle, CalendarClock, CheckCircle2, PlayCircle, User, ArrowRight, Plus, ClipboardList, ShieldAlert } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

function Stat({ icon, label, value, tone = "default", testId }) {
  const tones = {
    default: "bg-white border-brand-linen",
    danger: "bg-brand-accent/5 border-brand-accent/30",
    warn: "bg-amber-50 border-amber-200",
    success: "bg-emerald-50/60 border-emerald-200",
  };
  return (
    <div data-testid={testId} className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <div className="text-brand-secondary flex items-center gap-1.5 text-[11px] uppercase tracking-widest">
        {icon} {label}
      </div>
      <div className="font-serif text-3xl text-brand-ink mt-1.5">{value ?? 0}</div>
    </div>
  );
}

function TaskRow({ t }) {
  const overdue = t.due_date && t.due_date < new Date().toISOString().slice(0, 10);
  return (
    <Link
      to={`/practice-ops/instances/${t.instance_id}`}
      data-testid={`po-task-${t.id}`}
      className="flex items-center justify-between gap-3 border-b border-brand-linen last:border-b-0 px-4 py-3 hover:bg-brand-linen/20 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="text-brand-ink text-sm truncate">{t.title}</div>
        <div className="text-brand-muted text-xs mt-0.5 flex items-center gap-3">
          {t.assign_role && <span className="uppercase tracking-widest text-[10px]">{t.assign_role}</span>}
          {t.due_date && (
            <span className={overdue ? "text-brand-accent font-medium" : ""}>
              Due {fmt(t.due_date)}
            </span>
          )}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-brand-muted flex-shrink-0" strokeWidth={1.5} />
    </Link>
  );
}

function TaskGroup({ title, tasks, icon, empty }) {
  return (
    <div className="rounded-2xl bg-white border border-brand-linen">
      <div className="px-4 py-3 border-b border-brand-linen flex items-center gap-2">
        {icon}
        <div className="text-[11px] uppercase tracking-widest text-brand-muted">{title}</div>
        <div className="ml-auto text-brand-muted text-xs">{tasks.length}</div>
      </div>
      {tasks.length === 0 ? (
        <div className="px-4 py-6 text-center text-brand-muted text-sm">{empty}</div>
      ) : (
        <div>{tasks.map((t) => <TaskRow key={t.id} t={t} />)}</div>
      )}
    </div>
  );
}

export default function PracticeOpsDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/practice-ops/dashboard");
        setData(res.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Could not load dashboard");
      }
    })();
  }, []);

  return (
    <Layout wide>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1">Practice Ops</div>
          <h1 className="font-serif text-4xl text-brand-ink">What needs done</h1>
          <p className="text-brand-body text-sm mt-2 max-w-2xl">
            Today's operational picture — overdue and due-today first, upcoming next, plus what's assigned to you.
            No patient data — workforce and governance operations only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/practice-ops/library"
            data-testid="po-nav-library"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-brand-bone px-4 py-2 text-sm hover:bg-brand-inkHover transition-colors"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Start a workflow
          </Link>
          <Link to="/practice-ops/history" data-testid="po-nav-history"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-linen bg-white px-4 py-2 text-sm text-brand-secondary hover:text-brand-ink hover:border-brand-ink/40 transition-colors">
            <ClipboardList className="w-3.5 h-3.5" strokeWidth={1.5} /> History
          </Link>
          {isAdmin && (
            <Link to="/practice-ops/admin" data-testid="po-nav-admin"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-linen bg-white px-4 py-2 text-sm text-brand-secondary hover:text-brand-ink hover:border-brand-ink/40 transition-colors">
              <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} /> Admin
            </Link>
          )}
        </div>
      </div>

      {err && <div data-testid="po-dashboard-error" className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            <Stat testId="po-stat-today" icon={<Clock className="w-3.5 h-3.5" strokeWidth={1.5} />} label="Today" value={data.counts.today} />
            <Stat testId="po-stat-overdue" icon={<AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />} label="Overdue" value={data.counts.overdue} tone={data.counts.overdue > 0 ? "danger" : "default"} />
            <Stat testId="po-stat-upcoming" icon={<CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} />} label="Upcoming" value={data.counts.upcoming} />
            <Stat testId="po-stat-completed" icon={<CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />} label="Completed" value={data.counts.completed} tone="success" />
            <Stat testId="po-stat-active" icon={<PlayCircle className="w-3.5 h-3.5" strokeWidth={1.5} />} label="Active workflows" value={data.counts.active_workflows} />
            <Stat testId="po-stat-mine" icon={<User className="w-3.5 h-3.5" strokeWidth={1.5} />} label="Assigned to me" value={data.counts.my_tasks} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <TaskGroup title="Overdue" icon={<AlertTriangle className="w-4 h-4 text-brand-accent" strokeWidth={1.5} />} tasks={data.overdue} empty="Nothing overdue — nice." />
            <TaskGroup title="Today" icon={<Clock className="w-4 h-4 text-brand-ink" strokeWidth={1.5} />} tasks={data.today} empty="Nothing due today." />
            <TaskGroup title="Upcoming (next up)" icon={<CalendarClock className="w-4 h-4 text-brand-secondary" strokeWidth={1.5} />} tasks={data.upcoming.slice(0, 10)} empty="No upcoming tasks." />
            <TaskGroup title="Assigned to me" icon={<User className="w-4 h-4 text-brand-secondary" strokeWidth={1.5} />} tasks={data.my_tasks} empty="You're all clear." />
          </div>
        </>
      )}
    </Layout>
  );
}
